import React, { useState, useRef, useEffect } from 'react';
import './styles/App.css';
import ImageCarousel from './components/ImageCarousel';
import StayConnectedForm from './components/StayConnectedForm';
import PartnersPage from './components/PartnersPage';

function App() {
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showStayConnected, setShowStayConnected] = useState(false);
  const [showPartnersPage, setShowPartnersPage] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const isAnimating = useRef(false);

  const goToSection2 = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setActiveSection(1);
    setTimeout(() => { isAnimating.current = false; }, 1400);
  };

  const goToSection1 = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setActiveSection(0);
    setTimeout(() => { isAnimating.current = false; }, 1400);
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (isAnimating.current) return;
      if (e.deltaY > 0) goToSection2();
      else goToSection1();
    };

    let touchStartY = 0;
    const handleTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const handleTouchEnd = (e) => {
      const diff = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 50) return;
      if (diff > 0) goToSection2();
      else goToSection1();
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleOpenPartnersPage = () => {
    setIsFading(true);
    setTimeout(() => { setShowPartnersPage(true); setIsFading(false); }, 2000);
  };

  const handleClosePartnersPage = () => {
    setIsFading(true);
    setTimeout(() => { setShowPartnersPage(false); setIsFading(false); }, 2000);
  };

  const handleOpenStayConnected = () => {
    setIsFading(true);
    setTimeout(() => { setShowStayConnected(true); setIsFading(false); }, 2000);
  };

  const handleCloseStayConnected = () => {
    setIsFading(true);
    setTimeout(() => { setShowStayConnected(false); setIsFading(false); }, 2000);
  };

  return (
    <div style={{ position: 'fixed', width: '100%', height: '100vh', overflow: 'auto' }}>
      <div
        className={`fixed inset-0 bg-black z-[10000] pointer-events-none transition-opacity duration-[2000ms] ${
          isFading ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {activeSection === 0 ? (
        <div
          className="fixed right-6 top-[90vh] -translate-y-1/2 z-[9999] cursor-pointer group"
          onClick={goToSection2}
        >
          <svg viewBox="0 0 30 30" className="text-[#D4A574] animate-bounce group-hover:text-[#B8935D] transition-colors w-10 h-10 md:w-12 md:h-12">
            <path d="M7 10L15 18L23 10" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <path d="M7 15L15 23L23 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5"/>
          </svg>
        </div>
      ) : (
        <div
          className="fixed right-6 top-[10vh] -translate-y-1/2 z-[9999] cursor-pointer group"
          onClick={goToSection1}
        >
          <svg viewBox="0 0 30 30" className="text-[#D4A574] animate-bounce group-hover:text-[#B8935D] transition-colors w-10 h-10 md:w-12 md:h-12">
            <path d="M7 18L15 10L23 18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <path d="M7 23L15 15L23 23" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5"/>
          </svg>
        </div>
      )}

      <section
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          zIndex: 1,
        }}
        className="flex items-center justify-center"
      >
        <ImageCarousel onEnterSite={goToSection2} />
      </section>

      <section
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          zIndex: 2,
          transition: 'transform 1.4s cubic-bezier(0.77, 0, 0.175, 1)',
          transform: activeSection === 1 ? 'translateY(0)' : 'translateY(100%)',
          overflowY: 'auto',
          backgroundColor: '#000',
        }}
        className="flex flex-col justify-center px-0 py-0"
      >
        <div className="hero-text -mt-10 sm:mt-0">
          <h1>
            <span className="line-1">THE CONTINUUM OF HERITAGE</span>
            <span className="line-2">SHAPING DESIGN</span>
          </h1>
          <p>A dynamic intersection of Design, Art, Architecture and Technology, rooted in the timeless expression of Africa's rich heritage.</p>
        </div>

        <div className="flex flex-wrap gap-4 sm:gap-6 justify-center px-4 -mt-12 relative z-10">
          <button
            onClick={handleOpenPartnersPage}
            disabled={isFading}
            className="w-36 xs:w-40 sm:w-52 md:w-60 lg:w-72 px-3 xs:px-4 sm:px-6 md:px-8 py-1.5 xs:py-2 sm:py-2.5 md:py-3.5 text-[9px] xs:text-[10px] sm:text-xs md:text-sm lg:text-base font-medium text-white border-2 border-white/80 rounded-full transition-all duration-300 ease-in-out uppercase tracking-wide hover:text-[#D4A574] hover:border-[#D4A574] hover:-translate-y-1 hover:scale-x-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Step Into the Vision
          </button>

          <button
            onClick={handleOpenStayConnected}
            disabled={isFading}
            className="w-36 xs:w-40 sm:w-52 md:w-60 lg:w-72 px-3 xs:px-4 sm:px-6 md:px-8 py-1.5 xs:py-2 sm:py-2.5 md:py-3.5 text-[9px] xs:text-[10px] sm:text-xs md:text-sm lg:text-base font-medium text-white border-2 border-white/80 rounded-full transition-all duration-300 ease-in-out uppercase tracking-wide hover:text-[#D4A574] hover:border-[#D4A574] hover:-translate-y-1 hover:scale-x-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Stay Connected
          </button>
        </div>
      </section>

      <PartnersPage isOpen={showPartnersPage} onClose={handleClosePartnersPage} />
      <StayConnectedForm isOpen={showStayConnected} onClose={handleCloseStayConnected} />

      {showPartnerForm && (
        <div className="modal-overlay" onClick={() => setShowPartnerForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPartnerForm(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

